import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AccordionTitle from '@app/ui/sports-components/AccordionTitle';
import AccordionIcon from '@app/ui/sports-components/AccordionIcon';
import AccordionChildItems from '@app/ui/sports-components/AccordionChildItems';
import AccordionItem from '@app/ui/sports-components/AccordionItem';
import AccountTag from '../text-tracking/AccountTag';
import TextTrackingStatusToggleCell from '../text-tracking/TextTrackingStatusToggleCell';
import TextFilter from '../../components/common/table-filters-legacy/TextFilter';
import Table from '../../components/common/TableLegacy';
import StatusFilter from '../../components/common/table-filters-legacy/StatusFilter';
import { NO_IMAGE } from '../../constants/fallback_image';

type RhTextTrackingConfigurationProps = {
    isLoading: boolean;
    data: {
        accounts: {
            platformId: number;
            username: string;
            activeSocialFilter: boolean;
            profileUrl: string;
        }[];
        rightsholderId: number;
        rightsholderName: string;
        rhLogo: string;
        rhTextTrackingFilters: {
            sponsorName: string;
            test: string;
            active: string;
        }[];
    };
    onChangeStatus: (ttfId: number, shouldActivate: boolean) => void;
    shouldExpand?: boolean;
};

const RhTextTrackingConfiguration = ({
    data,
    isLoading,
    onChangeStatus,
    shouldExpand = false,
}: RhTextTrackingConfigurationProps) => {
    const { t } = useTranslation('settings');
    const [isExpanded, setIsExpanded] = useState(shouldExpand);

    const triggerCollapse = () => {
        setIsExpanded(!isExpanded);
    };

    const getStatusToggleCell = useCallback(({ row }) => {
        return (
            <TextTrackingStatusToggleCell
                ttfId={row._original.rightsholderTextTrackingFilterId}
                active={row.active}
                onStatusToggled={onChangeStatus}
            />
        );
    }, []);

    const columns = useMemo(
        () => [
            {
                Header: t('clientConfiguration.textTracking.table.sponsor'),
                accessor: 'sponsorName',
                ...TextFilter,
            },
            {
                Header: t('clientConfiguration.textTracking.table.text'),
                accessor: 'text',
                ...TextFilter,
            },
            {
                Header: t('clientConfiguration.textTracking.table.status'),
                accessor: 'active',
                Cell: getStatusToggleCell,
                ...StatusFilter,
                style: {
                    overflow: 'visible',
                },
            },
        ],
        [],
    );

    return (
        <AccordionItem
            data-test="rh-text-tracking-item"
            className="rightsholder-text-tracking-accordion -overflow-visible"
            isActive={isExpanded}
        >
            <AccordionTitle
                size="sm"
                context="secondary"
                onClick={triggerCollapse}
            >
                <div className="gds-flex gds-flex--justify-start gds-flex--align-center">
                    <div className="gds-flex__item gds-flex__item--grow-0">
                        <div
                            className="gds-account__image -m-r-2"
                            style={{
                                width: '2rem',
                                height: '2rem',
                            }}
                        >
                            <img
                                alt="rh-logo"
                                src={data.rhLogo || NO_IMAGE}
                                height="100%"
                                width="100%"
                            />
                        </div>
                    </div>
                    <div className="decorate decorate--all gds-flex__item gds-flex__item--grow-0">
                        {data.rightsholderName}
                    </div>
                </div>
            </AccordionTitle>
            <AccordionIcon
                isWhite
                size="sm"
                className="--cursor-pointer"
                onClick={triggerCollapse}
            />
            {isExpanded && (
                <AccordionChildItems
                    className="-p-h-3 -p-v-3"
                    data-test={'accordion-child-item'}
                >
                    <div
                        className="-overflow-y-scroll"
                        style={{ maxHeight: '300px' }}
                    >
                        {data.accounts?.map(account => (
                            <AccountTag
                                key={account.username + account.platformId}
                                account={account}
                            />
                        ))}
                    </div>
                    <div data-test="ttf-table">
                        <Table
                            columns={columns}
                            data={data.rhTextTrackingFilters}
                            loading={isLoading}
                            showPagination
                            isStriped
                            isHighlight
                            isSecondary
                            autoResetHiddenColumns={false}
                            pageSize={25}
                        />
                    </div>
                </AccordionChildItems>
            )}
        </AccordionItem>
    );
};

export default RhTextTrackingConfiguration;
